import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';

export default function CompatibilitySearch({
  selectedChemical,
  selectedMaterial,
  onChemicalSelect,
  onMaterialSelect,
}) {
  const [chemicalSearch, setChemicalSearch] = useState('');
  const [materialSearch, setMaterialSearch] = useState('');
  const [showChemicalResults, setShowChemicalResults] = useState(false);
  const [showMaterialResults, setShowMaterialResults] = useState(false);

  const chemicals = [
    { id: 1, name: 'Sulfuric Acid (H2SO4)', cas: '7664-93-9', concentration: '98%' },
    { id: 2, name: 'Hydrochloric Acid (HCl)', cas: '7647-01-0', concentration: '37%' },
    { id: 3, name: 'Sodium Hydroxide (NaOH)', cas: '1310-73-2', concentration: '50%' },
    { id: 4, name: 'Acetone', cas: '67-64-1', concentration: '99%' },
    { id: 5, name: 'Methanol', cas: '67-56-1', concentration: '99.9%' },
  ];

  const materials = [
    { id: 1, name: 'Stainless Steel 316', type: 'Metal', grade: '316L' },
    { id: 2, name: 'PTFE (Teflon)', type: 'Polymer', grade: 'Virgin' },
    { id: 3, name: 'Polypropylene', type: 'Polymer', grade: 'PP' },
    { id: 4, name: 'Carbon Steel', type: 'Metal', grade: 'A36' },
    { id: 5, name: 'EPDM Rubber', type: 'Elastomer', grade: 'Standard' },
  ];

  const filteredChemicals = chemicals?.filter(
    (c) =>
      c?.name?.toLowerCase()?.includes(chemicalSearch?.toLowerCase()) ||
      c?.cas?.includes(chemicalSearch)
  );

  const filteredMaterials = materials?.filter(
    (m) =>
      m?.name?.toLowerCase()?.includes(materialSearch?.toLowerCase()) ||
      m?.type?.toLowerCase()?.includes(materialSearch?.toLowerCase())
  );

  return (
    <div className="grid lg:grid-cols-2 gap-6 mb-6">
      {/* Chemical Selection */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Flask" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Select Chemical</h3>
            <p className="text-sm text-muted-foreground">Search by name or CAS number</p>
          </div>
        </div>

        <div className="relative">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={chemicalSearch}
              onChange={(e) => {
                setChemicalSearch(e?.target?.value);
                setShowChemicalResults(true);
              }}
              onFocus={() => setShowChemicalResults(true)}
              placeholder="Search chemicals..."
              className="pl-10"
            />
          </div>

          {showChemicalResults && chemicalSearch && (
            <div className="absolute z-10 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-auto">
              {filteredChemicals?.map((chemical) => (
                <button
                  key={chemical?.id}
                  onClick={() => {
                    onChemicalSelect(chemical);
                    setChemicalSearch(chemical?.name);
                    setShowChemicalResults(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-accent transition-colors border-b border-border last:border-0"
                >
                  <div className="font-medium text-foreground">{chemical?.name}</div>
                  <div className="text-sm text-muted-foreground flex gap-3 mt-1">
                    <span>CAS: {chemical?.cas}</span>
                    <span>Conc: {chemical?.concentration}</span>
                  </div>
                </button>
              ))}
              {filteredChemicals?.length === 0 && (
                <div className="px-4 py-3 text-muted-foreground text-center">
                  No chemicals found
                </div>
              )}
            </div>
          )}
        </div>

        {selectedChemical && (
          <div className="mt-4 p-3 bg-accent/50 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-foreground">{selectedChemical?.name}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  CAS: {selectedChemical?.cas} | Conc: {selectedChemical?.concentration}
                </div>
              </div>
              <button
                onClick={() => {
                  onChemicalSelect(null);
                  setChemicalSearch('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Material Selection */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <Icon name="Box" size={24} className="text-blue-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Select Material</h3>
            <p className="text-sm text-muted-foreground">Search by name or material type</p>
          </div>
        </div>

        <div className="relative">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={materialSearch}
              onChange={(e) => {
                setMaterialSearch(e?.target?.value);
                setShowMaterialResults(true);
              }}
              onFocus={() => setShowMaterialResults(true)}
              placeholder="Search materials..."
              className="pl-10"
            />
          </div>

          {showMaterialResults && materialSearch && (
            <div className="absolute z-10 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-auto">
              {filteredMaterials?.map((material) => (
                <button
                  key={material?.id}
                  onClick={() => {
                    onMaterialSelect(material);
                    setMaterialSearch(material?.name);
                    setShowMaterialResults(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-accent transition-colors border-b border-border last:border-0"
                >
                  <div className="font-medium text-foreground">{material?.name}</div>
                  <div className="text-sm text-muted-foreground flex gap-3 mt-1">
                    <span>Type: {material?.type}</span>
                    <span>Grade: {material?.grade}</span>
                  </div>
                </button>
              ))}
              {filteredMaterials?.length === 0 && (
                <div className="px-4 py-3 text-muted-foreground text-center">
                  No materials found
                </div>
              )}
            </div>
          )}
        </div>

        {selectedMaterial && (
          <div className="mt-4 p-3 bg-accent/50 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-foreground">{selectedMaterial?.name}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Type: {selectedMaterial?.type} | Grade: {selectedMaterial?.grade}
                </div>
              </div>
              <button
                onClick={() => {
                  onMaterialSelect(null);
                  setMaterialSearch('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}